import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { AngularFirestore, AngularFirestoreDocument, QuerySnapshot } from '@angular/fire/compat/firestore';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';

import app from 'firebase/compat/app';
import { Subscription, firstValueFrom } from 'rxjs';

import { TerpecaNomination } from 'src/app/models/nomination.model';
import { PartnerImportOption } from 'src/app/models/partner-played.model';
import { maxQuotesPerYear } from 'src/app/models/quote.model';
import {
    BallotRoomIds, TerpecaRanking, UnrankedReason, UnrankedReasonEntry, UnrankedReasonMap, getUnrankedIds,
    getUnrankedReasonDescription, getUnrankedReasonEmoji
} from 'src/app/models/ranking.model';
import { TerpecaCategory, TerpecaRoom } from 'src/app/models/room.model';
import { ApplicationStatus, HorrorPreference, TerpecaUser, getTrackerPartner } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { PartnerImportService } from 'src/app/services/partner-import.service';
import { SettingsService } from 'src/app/services/settings.service';
import * as utils from 'src/app/utils/misc.utils';
import { CombinedPrompt, combinedPrompt, combinedRoomsWithPart, listedParts, roomDisplayName } from 'src/app/utils/combined-rooms.utils';
import { sortImportOptions } from 'src/app/utils/partner-import.utils';
import { compareEntitiesByLocation, compareEntitiesByName, compareRoomsByCompany, compareUsingMap } from 'src/app/utils/sorting.utils';
import { ConfirmListDialogComponent } from 'src/app/views/confirmlistdialog/confirmlistdialog.component';
import { PartnerImportDialogComponent, PartnerImportTarget } from 'src/app/views/partnerimportdialog/partnerimportdialog.component';
import { QuoteComponent } from 'src/app/views/quote/quote.component';
import { environment } from 'src/environments/environment';

import { SimpleDialogComponent } from '../simpledialog/simpledialog.component';
import { UnrankRoomDialogComponent } from '../unrankroomdialog/unrankroomdialog.component';

@Component({
  selector: 'app-rank',
  standalone: false,
  templateUrl: './rank.component.html',
  styleUrl: './rank.component.css'
})
export class RankComponent implements OnInit, OnDestroy, PartnerImportTarget {
  auth = inject(AuthService);
  settings = inject(SettingsService);
  private db = inject(AngularFirestore);
  private functions = inject(AngularFireFunctions);
  private dialog = inject(MatDialog);
  private partnerImport = inject(PartnerImportService);

  user: TerpecaUser;
  allRooms: TerpecaRoom[];
  rankableRegularRooms: TerpecaRoom[];
  rankableOnlineRooms: TerpecaRoom[];
  rankableRoomMap: Map<string, TerpecaRoom>;
  allCountryList: string[];
  irlCountryList: string[];
  onlineCountryList: string[];
  countryDisplayName = utils.countryDisplayName;

  currentSortFn: (a: (TerpecaRoom | TerpecaNomination), b: (TerpecaRoom | TerpecaNomination)) => number; // contextual
  currentCategory: TerpecaCategory;      // contextual
  selectedRoomIds: string[];             // contextual
  rankedRoomIds: string[];               // contextual
  stillToRankRoomIds: string[] = [];     // contextual, derived from the other lists
  unrankedRoomIds: string[];             // contextual
  unrankedReasonMap: UnrankedReasonMap;  // contextual

  regularPlayedIds: Set<string>;
  onlinePlayedIds: Set<string>;
  regularNominationIds: string[];
  onlineNominationsIds: string[];
  previousRegularRanking: TerpecaRanking;
  previousOnlineRanking: TerpecaRanking;

  regularRanking: TerpecaRanking;
  onlineRanking: TerpecaRanking;

  firstFormGroup: UntypedFormGroup;
  secondFormGroup: UntypedFormGroup;
  thirdFormGroup: UntypedFormGroup;
  fourthFormGroup: UntypedFormGroup;
  fifthFormGroup: UntypedFormGroup;
  sixthFormGroup: UntypedFormGroup;
  private regularRankingStepIndex = 3;
  private onlineRankingStepIndex: number;
  addQuotesStepIndex: number;
  reviewAndSubmitStepIndex: number;
  furthestStepReached = 0;
  year = environment.currentAwardYear;
  regularRankingDoc: AngularFirestoreDocument<TerpecaRanking>;
  onlineRankingDoc: AngularFirestoreDocument<TerpecaRanking>;
  private userSubscription: Subscription;
  private regularRankingSubscription: Subscription;
  private onlineRankingSubscription: Subscription;
  hideRules = false;
  confirmedRules = false;
  readyToStart = false;
  readyToRank = false;
  rankingNotes: string[];
  rankingErrors: string[];
  Status = ApplicationStatus;
  roomCountrySort = compareEntitiesByLocation;
  roomNameSort = compareEntitiesByName;
  roomCompanySort = compareRoomsByCompany;
  Category = TerpecaCategory;
  importOptions: PartnerImportOption[] = [];
  linkedImportSites: { option: PartnerImportOption; id: string }[] = [];
  linkedImportSiteNames = '';
  allImportSiteNames = '';
  getUnrankedIds = getUnrankedIds;

  @ViewChild('stepper') stepper: MatStepper;
  @ViewChildren(QuoteComponent) quoteComponents: QueryList<QuoteComponent>;
  bioUpdated = false;

  constructor() {
    this.firstFormGroup = new UntypedFormGroup({
      realName: new UntypedFormControl('', Validators.required),
      irlRoomCount: new UntypedFormControl('', Validators.required),
      country: new UntypedFormControl('', Validators.required),
      state: new UntypedFormControl(''),
      city: new UntypedFormControl('', Validators.required),
      bio: new UntypedFormControl('', Validators.required),
      horrorPreference: new UntypedFormControl('', Validators.required),
      videoContributionInterest: new UntypedFormControl('', Validators.required)
    });
    this.secondFormGroup = new UntypedFormGroup({ searchText: new UntypedFormControl(''), countryFilter: new UntypedFormControl('') });  // disclose affiliations
    this.thirdFormGroup = new UntypedFormGroup({ searchText: new UntypedFormControl(''), countryFilter: new UntypedFormControl('') });   // select regular rooms
    this.fourthFormGroup = new UntypedFormGroup({ });  // rank regular rooms
    if (this.settings.hasOnlineRoomCategory()) {
      this.fifthFormGroup = new UntypedFormGroup({ searchText: new UntypedFormControl(''), countryFilter: new UntypedFormControl('') }); // select online rooms
      this.sixthFormGroup = new UntypedFormGroup({ });   // rank online rooms
      this.onlineRankingStepIndex = 5;
      this.addQuotesStepIndex = 6;
      this.reviewAndSubmitStepIndex = 7;
    } else {
      this.onlineRankingStepIndex = -1;
      this.addQuotesStepIndex = 4;
      this.reviewAndSubmitStepIndex = 5;
    }
  }

  ngOnInit() {
    this.userSubscription = this.auth.user.subscribe(async (user: TerpecaUser) => {
      if (user && (!this.user || (user.uid !== this.user.uid))) {
        if (this.regularRankingSubscription) {
          this.regularRankingSubscription.unsubscribe();
        }
        if (this.onlineRankingSubscription) {
          this.onlineRankingSubscription.unsubscribe();
        }
        if (user.status >= ApplicationStatus.VOTER && this.settings.isVotingOpen(user)) {
          console.log(`Loading rooms for ${user.email}`);
          await Promise.all([this.loadRooms(), this.loadImportOptions()]);
          console.log('Loading past nominations and rankings');
          this.regularPlayedIds = new Set<string>();
          this.regularNominationIds = await this.loadNominations(user, TerpecaCategory.TOP_ROOM);
          this.previousRegularRanking = await this.loadPreviousRankings(user, TerpecaCategory.TOP_ROOM);
          if (this.settings.hasOnlineRoomCategory()) {
            this.onlinePlayedIds = new Set<string>();
            this.onlineNominationsIds = await this.loadNominations(user, TerpecaCategory.TOP_ONLINE_ROOM);
            this.previousOnlineRanking = await this.loadPreviousRankings(user, TerpecaCategory.TOP_ONLINE_ROOM);
          }
          console.log('Loading rankings');
          await this.loadRankings(user, TerpecaCategory.TOP_ROOM);
          if (this.settings.hasOnlineRoomCategory()) {
            await this.loadRankings(user, TerpecaCategory.TOP_ONLINE_ROOM);
          }
          console.log('Loading complete');
        }
      }
      this.user = user;
      this.refreshImportLines();
      if (user) {
        this.firstFormGroup.patchValue(user);
        this.hideRules = this.confirmedRules = user.rankingRulesConfirmed?.includes(this.year) || false;
      }
      this.readyToStart = true;
    });
  }

  private async loadRooms() {
    if (this.rankableRoomMap) {
      return;
    }
    const allCountrySet = new Set<string>();
    const irlCountrySet = new Set<string>();
    const onlineCountrySet = new Set<string>();
    await this.loadNomineeRooms()
    .then((rooms: TerpecaRoom[]) => {
      for (const room of rooms) {
        allCountrySet.add(room.country);
      }
      this.allRooms = rooms;
      const rankableRegularRooms: TerpecaRoom[] = [];
      const rankableOnlineRooms: TerpecaRoom[] = [];
      const rankableRoomMap = new Map<string, TerpecaRoom>();
      for (const room of rooms) {
        if (room.isFinalist?.includes(this.year)) {
          switch (room.category) {
            case TerpecaCategory.TOP_ROOM:
              rankableRegularRooms.push(room);
              irlCountrySet.add(room.country);
              break;
            case TerpecaCategory.TOP_ONLINE_ROOM:
              if (this.settings.hasOnlineRoomCategory()) {
                rankableOnlineRooms.push(room);
                onlineCountrySet.add(room.country);
              }
              break;
          }
          rankableRoomMap.set(room.docId, room);
        }
      }
      this.rankableRegularRooms = rankableRegularRooms;
      this.rankableOnlineRooms = rankableOnlineRooms;
      this.rankableRoomMap = rankableRoomMap;
      this.allCountryList = Array.from(allCountrySet).sort();
      this.irlCountryList = Array.from(irlCountrySet).sort();
      this.onlineCountryList = Array.from(onlineCountrySet).sort();
      console.log(`loaded ${rooms.length} rooms: ${rankableRegularRooms.length} regular rooms and ` +
        `${rankableOnlineRooms.length} online rooms are rankable`);
    });
  }

  // This year's nominee and finalist flags only reach the public room docs when voting opens for everyone, so early
  // voters (reviewers) can't find them there. Everyone gets the ids from a callable instead, so early voting runs the
  // same code as regular voting, and loads those rooms by id.
  private async loadNomineeRooms(): Promise<TerpecaRoom[]> {
    const toRooms = (snapshot: QuerySnapshot<TerpecaRoom>) => snapshot.docs.map(doc => {
      const room: TerpecaRoom = doc.data();
      room.docId = doc.id;
      return room;
    });
    const ids = await firstValueFrom(
      this.functions.httpsCallable<Record<string, never>, BallotRoomIds>('getBallotRoomIds')({}));
    const finalistIds = new Set(ids.finalistIds);
    const chunks: string[][] = [];
    for (let i = 0; i < ids.nomineeIds.length; i += 30) {  // the most an 'in' query allows
      chunks.push(ids.nomineeIds.slice(i, i + 30));
    }
    const snapshots = await Promise.all(chunks.map(async chunk => this.db.collection<TerpecaRoom>('rooms').ref
      .where(app.firestore.FieldPath.documentId(), 'in', chunk).get()));
    const rooms = snapshots.flatMap(toRooms);
    for (const room of rooms) {
      room.isNominee = [...(room.isNominee || []).filter(year => year !== this.year), this.year];
      if (finalistIds.has(room.docId)) {
        room.isFinalist = [...(room.isFinalist || []).filter(year => year !== this.year), this.year];
      }
    }
    return rooms;
  }

  private async loadNominations(user: TerpecaUser, category: TerpecaCategory) {
    const nominations: string[] = [];
    await this.db.collection<TerpecaNomination>('nominations').ref
    .where('pending', '==', false)
    .where('category', '==', category)
    .where('userId', '==', user.uid).get()
    .then((snapshot: QuerySnapshot<TerpecaNomination>) => {
      for (const doc of snapshot.docs) {
        const nom: TerpecaNomination = doc.data();
        switch (category) {
          case TerpecaCategory.TOP_ROOM:
            this.regularPlayedIds.add(nom.roomId);
            break;
          case TerpecaCategory.TOP_ONLINE_ROOM:
            this.onlinePlayedIds.add(nom.roomId);
            break;
        }
        if (nom.year === environment.currentAwardYear && !nominations.includes(nom.roomId)) {
          nominations.push(nom.roomId);
        }
      }
    });
    return nominations;
  }

  private async loadPreviousRankings(user: TerpecaUser, category: TerpecaCategory) {
    // When creating a new year's ranking, we find the last ranking of the same type to seed the data.
    let prevRanking: TerpecaRanking = null;
    for (let y = this.year - 1; y >= this.settings.allYears[0]; --y) {
      if (user.rankingsSubmitted?.includes(y)) {
        const ranking = await this.db.collection<TerpecaRanking>('rankings').doc(`${user.uid}-${y}-${category}`).ref
        .get().then(prevDoc => (prevDoc.exists ? <TerpecaRanking>prevDoc.data() : null));
        if (ranking) {
          if (!prevRanking) {
            prevRanking = ranking;
          }
          if (ranking.unsortedIds) {
            for (const roomId of ranking.unsortedIds) {
              switch (category) {
                case TerpecaCategory.TOP_ROOM:
                  this.regularPlayedIds.add(roomId);
                  break;
                case TerpecaCategory.TOP_ONLINE_ROOM:
                  this.onlinePlayedIds.add(roomId);
                  break;
              }
            }
          }
        }
      }
    }
    if (!prevRanking) {
      console.log(`no previous rankings found for category ${category}`);
    }
    return prevRanking;
  }

  private async loadRankings(user: TerpecaUser, category: TerpecaCategory) {
    const docId = `${user.uid}-${this.year}-${category}`;
    const doc: AngularFirestoreDocument<TerpecaRanking> = this.db.collection<TerpecaRanking>('rankings').doc(docId);
    const subscription: Subscription = doc.valueChanges()
    .subscribe(async (storedRanking: TerpecaRanking) => {
      if (!storedRanking && !this.settings.isPastVotingDeadline()) {
        console.log(`creating initial ${this.year} ranking doc for category ${category}`);
        // We have to be super sure that the doc doesn't exist so we don't overwrite good data.
        await doc.ref.get().then(async d => {
          if (!d.exists) {
            // We load any nominations in this category to use to help seed the rooms played.
            const newRanking: TerpecaRanking = {
              createTime: <app.firestore.Timestamp>app.firestore.FieldValue.serverTimestamp(),
              userId: user.uid,
              year: environment.currentAwardYear,
              category,
              horrorPreference: user.horrorPreference || HorrorPreference.UNKNOWN,
              unsortedIds: [],
              rankedIds: [],
              unrankedReasonMap: {},
              submitted: false,
              shadowbanned: user.shadowbanned || false
            };
            // Rooms the user is affiliated with are never seeded into their lists.
            const affiliatedIds = new Set(user.affiliatedRoomIds || []);
            const isSeedable = (roomId: string) => this.rankableRoomMap.has(roomId) && !affiliatedIds.has(roomId);
            // When creating a new year's ranking, we use the last ranking of the same type to seed the data.
            const prevRanking = (category === TerpecaCategory.TOP_ROOM ? this.previousRegularRanking : this.previousOnlineRanking);
            if (prevRanking && prevRanking.unsortedIds && prevRanking.rankedIds) {
              newRanking.unsortedIds = [...prevRanking.unsortedIds].filter(isSeedable);
              newRanking.rankedIds = [...prevRanking.rankedIds].filter(isSeedable);
              const prevUnrankedIds = getUnrankedIds(prevRanking).filter(isSeedable);
              newRanking.unrankedReasonMap = {};
              for (const roomId of Object.keys(prevRanking.unrankedReasonMap || {})) {
                if (prevUnrankedIds.includes(roomId)) {
                  newRanking.unrankedReasonMap[roomId] = prevRanking.unrankedReasonMap[roomId];
                }
              }
              newRanking.seededFrom = prevRanking.year;
              console.log(`seeding rankings for category ${category} from ${prevRanking.year}`);
            }
            // We also add any nominated rooms to the rooms played list if they haven't been added yet.
            const nominatedRoomIds = (category === TerpecaCategory.TOP_ROOM ? this.regularNominationIds : this.onlineNominationsIds);
            let nominatedRoomsAdded = 0;
            for (const roomId of nominatedRoomIds) {
              if (isSeedable(roomId) && !newRanking.unsortedIds.includes(roomId)) {
                newRanking.unsortedIds.push(roomId);
                nominatedRoomsAdded += 1;
              }
            }
            if (nominatedRoomsAdded > 0) {
              console.log(`added ${nominatedRoomsAdded} nominated rooms to the played list for category ${category}`);
            }
            // Finally we add any unaffiliated rooms nominated or ranked in past years if they haven't been added yet.
            const playedIds = (category === TerpecaCategory.TOP_ROOM ? this.regularPlayedIds : this.onlinePlayedIds);
            let pastRoomsAdded = 0;
            for (const roomId of playedIds.keys()) {
              if (isSeedable(roomId) && !newRanking.unsortedIds.includes(roomId)) {
                newRanking.unsortedIds.push(roomId);
                pastRoomsAdded += 1;
              }
            }
            if (pastRoomsAdded > 0) {
              console.log(`added ${pastRoomsAdded} previously ranked or nominated rooms to the played list for category ${category}`);
            }
            await doc.set(newRanking);
          }
        });
        return;
      }
      switch (category) {
        case TerpecaCategory.TOP_ROOM:
          this.regularRanking = storedRanking;
          break;
        case TerpecaCategory.TOP_ONLINE_ROOM:
          this.onlineRanking = storedRanking;
          break;
      }
    });
    switch (category) {
      case TerpecaCategory.TOP_ROOM:
        this.regularRankingDoc = doc;
        this.regularRankingSubscription = subscription;
        break;
      case TerpecaCategory.TOP_ONLINE_ROOM:
        this.onlineRankingDoc = doc;
        this.onlineRankingSubscription = subscription;
        break;
    }
  }

  lastVoterYear() {
    return utils.lastVoterYear(this.user, this.year);
  }

  nominatedThisYear() {
    return this.user.nominationsSubmitted?.includes(this.year);
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe();
    if (this.regularRankingSubscription) {
      this.regularRankingSubscription.unsubscribe();
    }
    if (this.onlineRankingSubscription) {
      this.onlineRankingSubscription.unsubscribe();
    }
  }

  toggleRules() {
    this.hideRules = !this.hideRules;
    window.scroll({
      top: 0,
      left: 0
    });
    if (this.hideRules && !this.user.rankingRulesConfirmed?.includes(this.year)) {
      this.auth.confirmRankingRules();
    }
  }

  async stepChanged(event: StepperSelectionEvent) {
    this.currentSortFn = this.roomCountrySort;
    this.readyToRank = false;
    if (event.selectedIndex === this.regularRankingStepIndex - 1) {
      await this.fixUpCombinedRooms(TerpecaCategory.TOP_ROOM);
    } else if (event.selectedIndex === this.onlineRankingStepIndex - 1) {
      await this.fixUpCombinedRooms(TerpecaCategory.TOP_ONLINE_ROOM);
    } else if (event.selectedIndex === this.regularRankingStepIndex) {
      await this.setupRankings(TerpecaCategory.TOP_ROOM);
    } else if (event.selectedIndex === this.onlineRankingStepIndex) {
      await this.setupRankings(TerpecaCategory.TOP_ONLINE_ROOM);
    } else if (event.selectedIndex === this.reviewAndSubmitStepIndex) {
      this.rankingErrors = [];
      this.validateRankings(TerpecaCategory.TOP_ROOM);
      if (this.settings.hasOnlineRoomCategory()) {
        this.validateRankings(TerpecaCategory.TOP_ONLINE_ROOM);
      }
    }
    this.furthestStepReached = Math.max(this.furthestStepReached, event.selectedIndex);
  }

  private async setupRankings(category: TerpecaCategory) {
    this.rankingNotes = [];
    let ranking: TerpecaRanking;
    switch (category) {
      case TerpecaCategory.TOP_ROOM:
        ranking = this.regularRanking;
        break;
      case TerpecaCategory.TOP_ONLINE_ROOM:
        ranking = this.onlineRanking;
        break;
    }
    if (!ranking) {
      console.log('Something went wrong - no ranking object loaded!');
      return;
    }
    this.currentCategory = category;
    this.selectedRoomIds = ranking.unsortedIds ? [...ranking.unsortedIds] : [];
    this.unrankedRoomIds = getUnrankedIds(ranking);
    this.unrankedReasonMap = ranking.unrankedReasonMap || { };
    // Selected rooms that aren't in rankedIds or unranked are "still to rank" until the voter places them.
    this.rankedRoomIds = ranking.rankedIds ? [...ranking.rankedIds] : [];
    let roomsDeleted = false;
    for (const id of this.rankedRoomIds) {
      if (!this.selectedRoomIds.includes(id)) {
        this.rankedRoomIds = this.rankedRoomIds.filter(item => item !== id);
        roomsDeleted = true;
      }
    }
    for (const id of this.unrankedRoomIds) {
      if (!this.selectedRoomIds.includes(id)) {
        this.unrankedRoomIds = this.unrankedRoomIds.filter(item => item !== id);
        roomsDeleted = true;
      }
    }
    if (roomsDeleted) {
      this.rankingNotes.push(
        'One or more rooms you previously ranked was removed from your played rooms list, and thus has been removed from the list.');
    }
    roomsDeleted = false;
    for (const id of this.rankedRoomIds) {
      if (!this.rankableRoomMap.has(id)) {
        this.rankedRoomIds = this.rankedRoomIds.filter(item => item !== id);
        roomsDeleted = true;
      }
    }
    for (const id of this.unrankedRoomIds) {
      if (!this.rankableRoomMap.has(id)) {
        this.unrankedRoomIds = this.unrankedRoomIds.filter(item => item !== id);
        roomsDeleted = true;
      }
    }
    if (roomsDeleted) {
      this.rankingNotes.push(
        'One or more rooms you previously ranked is no longer eligible to be ranked and has been removed from the list.');
    }
    this.refreshStillToRank();
    // It is possible that a room was removed from the unrankedRoomIds list without having been removed from the unrankedReasonMap
    // before we get here, so we fix that now.
    let reasonMapCleanedUp = false;
    for (const id of Object.keys(this.unrankedReasonMap)) {
      if (!this.unrankedRoomIds?.includes(id)) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete this.unrankedReasonMap[id];
        reasonMapCleanedUp = true;
      }
    }
    if (this.rankingNotes.length > 0 || reasonMapCleanedUp) {
      await this.saveRankingData();
    }
    this.readyToRank = true;
  }

  async saveApplicationData() {
    if (!this.firstFormGroup.valid) {
      return;
    }
    await this.auth.updateUser(this.firstFormGroup.value);
    this.firstFormGroup.markAsPristine();
    this.stepper.selected.completed = true;
    this.stepper.next();
  }

  isAffiliatedRoom(roomId: string) {
    return this.user.affiliatedRoomIds && this.user.affiliatedRoomIds.includes(roomId);
  }

  affiliationsDisclosed() {
    return this.user.affiliatedRoomIds;
  }

  hasAffiliatedRooms() {
    return this.user.affiliatedRoomIds && this.user.affiliatedRoomIds.length > 0;
  }

  numAffiliatedRooms() {
    return this.user.affiliatedRoomIds ? this.user.affiliatedRoomIds.length : 0;
  }

  toggleAffiliatedRoom(roomId: string) {
    if (!this.isAffiliatedRoom(roomId)) {
      this.markAffiliated(roomId);
    } else {
      this.markUnaffiliated(roomId);
    }
    this.secondFormGroup.markAsDirty();
  }

  affiliatedRooms() {
    const affiliatedRooms: TerpecaRoom[] = [];
    if (this.hasAffiliatedRooms()) {
      for (const room of this.allRooms) {
        if (room.docId && this.user.affiliatedRoomIds.includes(room.docId)) {
          affiliatedRooms.push(room);
        }
      }
    }
    affiliatedRooms.sort(this.roomCountrySort);
    return affiliatedRooms;
  }

  async markAffiliated(roomId: string) {
    this.removeRoomId(roomId);
    const batch = this.db.firestore.batch();
    batch.update(this.db.firestore.collection('rooms').doc(roomId), {
      affiliatedUserIds: <string[]><unknown>app.firestore.FieldValue.arrayUnion(this.user.uid)
    });
    batch.update(this.db.firestore.collection('users').doc(this.user.uid), {
      affiliatedRoomIds: <string[]><unknown>app.firestore.FieldValue.arrayUnion(roomId)
    });
    batch.update(this.regularRankingDoc.ref, {
      unsortedIds: <string[]><unknown>app.firestore.FieldValue.arrayRemove(roomId),
      rankedIds: <string[]><unknown>app.firestore.FieldValue.arrayRemove(roomId)
    });
    if (this.onlineRankingDoc) {
      batch.update(this.onlineRankingDoc.ref, {
        unsortedIds: <string[]><unknown>app.firestore.FieldValue.arrayRemove(roomId),
        rankedIds: <string[]><unknown>app.firestore.FieldValue.arrayRemove(roomId)
      });
    }
    await batch.commit();
  }

  async markUnaffiliated(roomId: string) {
    const batch = this.db.firestore.batch();
    batch.update(this.db.firestore.collection('rooms').doc(roomId), {
      affiliatedUserIds: <string[]><unknown>app.firestore.FieldValue.arrayRemove(this.user.uid)
    });
    batch.update(this.db.firestore.collection('users').doc(this.user.uid), {
      affiliatedRoomIds: <string[]><unknown>app.firestore.FieldValue.arrayRemove(roomId)
    });
    await batch.commit();
  }

  async confirmAffiliations() {
    if (!this.hasAffiliatedRooms()) {
      await this.db.firestore.collection('users').doc(this.user.uid).update({
        affiliatedRoomIds: []
      });
    }
    const dialogRef = this.dialog.open(ConfirmListDialogComponent, {
      data: {
        category: 'Affiliations',
        confirmNoneInstructions: 'Please confirm that you have no rooms in the list with which you are affiliated.',
        confirmInstructions: 'Please confirm that the following is a complete list of the rooms with which you are affiliated.',
        roomList: this.affiliatedRooms(),
        numbered: false
      }
    });
    const subscription = dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.secondFormGroup.markAsPristine();
        this.stepper.selected.completed = true;
        this.stepper.next();
      }
      subscription.unsubscribe();
    });
  }

  private getFirestoreDoc(category: TerpecaCategory): AngularFirestoreDocument<TerpecaRanking> {
    switch (category) {
      case TerpecaCategory.TOP_ROOM:
        return this.regularRankingDoc;
      case TerpecaCategory.TOP_ONLINE_ROOM:
        return this.onlineRankingDoc;
    }
  }

  private getRanking(category: TerpecaCategory): TerpecaRanking {
    switch (category) {
      case TerpecaCategory.TOP_ROOM:
        return this.regularRanking;
      case TerpecaCategory.TOP_ONLINE_ROOM:
        return this.onlineRanking;
    }
  }

  private getPreviousRanking(category: TerpecaCategory): TerpecaRanking {
    switch (category) {
      case TerpecaCategory.TOP_ROOM:
        return this.previousRegularRanking;
      case TerpecaCategory.TOP_ONLINE_ROOM:
        return this.previousOnlineRanking;
    }
  }

  maxResultsToShow() {
    return !this.secondFormGroup.value.countryFilter && !this.secondFormGroup.value.searchText && this.allRooms?.length > 100 ? 100 : 0;
  }

  roomsSelected(category: TerpecaCategory) {
    const ranking: TerpecaRanking = this.getRanking(category);
    return ranking && ranking.unsortedIds;
  }

  isPlayedRoom(category: TerpecaCategory, roomId: string) {
    const ranking: TerpecaRanking = this.getRanking(category);
    return ranking && ranking.unsortedIds && ranking.unsortedIds.includes(roomId);
  }

  hasPlayedRooms(category: TerpecaCategory) {
    const ranking: TerpecaRanking = this.getRanking(category);
    return ranking && ranking.unsortedIds && ranking.unsortedIds.length > 0;
  }

  numPlayedRooms(category: TerpecaCategory) {
    const ranking: TerpecaRanking = this.getRanking(category);
    return ranking && ranking.unsortedIds ? ranking.unsortedIds.length : 0;
  }

  roomSelectLabel(roomId: string) {
    if (this.isAffiliatedRoom(roomId)) {
      return 'Affiliated';
    }
    return 'Mark Played';
  }

  roomUnselectLabel(roomId: string) {
    if (this.isAffiliatedRoom(roomId)) {
      return 'Affiliated';
    }
    return 'Mark Unplayed';
  }

  async togglePlayedRoom(category: TerpecaCategory, roomId: string) {
    if (!this.isPlayedRoom(category, roomId)) {
      await this.markPlayedWithCombined(category, roomId);
    } else if (!await this.markUnplayedWithCombined(category, roomId)) {
      return;
    }
    switch (category) {
      case TerpecaCategory.TOP_ROOM:
        this.thirdFormGroup.markAsDirty();
        break;
      case TerpecaCategory.TOP_ONLINE_ROOM:
        this.fifthFormGroup.markAsDirty();
        break;
    }
  }

  playedRooms(category: TerpecaCategory) {
    const ranking: TerpecaRanking = this.getRanking(category);
    const playedRooms: TerpecaRoom[] = [];
    if (this.hasPlayedRooms(category)) {
      for (const room of this.allRooms) {
        if (room.docId && ranking.unsortedIds.includes(room.docId)) {
          playedRooms.push(room);
        }
      }
    }
    playedRooms.sort(this.roomCountrySort);
    return playedRooms;
  }

  quotableRooms() {
    return this.regularRanking?.rankedIds?.slice(0, maxQuotesPerYear);
  }

  hasUnsavedQuotes(): boolean {
    if (!this.quoteComponents) {
      return false;
    }
    return this.quoteComponents.some(comp => comp.formGroup && comp.formGroup.dirty);
  }

  showNewLogo(roomId: string) {
    const previousRanking = this.getPreviousRanking(this.currentCategory);
    if (!previousRanking || !previousRanking.submitted) {
      // In this case, all rooms are new, so the distinction isn't helpful.
      return false;
    }
    return !previousRanking.rankedIds?.includes(roomId);
  }

  getUnrankedEmoji(category: TerpecaCategory, roomId: string) {
    const ranking: TerpecaRanking = this.getRanking(category);
    return ranking?.unrankedReasonMap ? getUnrankedReasonEmoji(ranking.unrankedReasonMap[roomId]) : '';
  }

  getUnrankedReason(category: TerpecaCategory, roomId: string) {
    const ranking: TerpecaRanking = this.getRanking(category);
    return ranking?.unrankedReasonMap ? getUnrankedReasonDescription(ranking.unrankedReasonMap[roomId]) : '';
  }

  async markPlayed(category: TerpecaCategory, ...roomIds: string[]) {
    await this.getFirestoreDoc(category).update({
      unsortedIds: <string[]><unknown>app.firestore.FieldValue.arrayUnion(...roomIds)
    });
  }

  async markUnplayed(category: TerpecaCategory, ...roomIds: string[]) {
    await this.getFirestoreDoc(category).update({
      unsortedIds: <string[]><unknown>app.firestore.FieldValue.arrayRemove(...roomIds)
    });
  }

  // Combined games: playing a combined game means playing its parts, so marking one marks them too. Marking the
  // last listed part of a combined game asks whether they played the combined version, which only counts if it was
  // booked and played as a single playthrough. Unmarking a combined game asks whether to unmark its parts too.
  private async markPlayedWithCombined(category: TerpecaCategory, roomId: string) {
    const room = this.rankableRoomMap.get(roomId);
    const addedParts = room ? this.unplayedParts(category, room) : [];
    await this.markPlayed(category, roomId, ...addedParts.map(part => part.docId));
    if (addedParts.length) {
      await this.showCombinedNotice(`Since ${this.roomName(room)} includes ${this.roomList(addedParts)}, ` +
        `we've marked ${addedParts.length === 1 ? 'it' : 'them'} as played too.`);
      return;
    }
    for (const combined of combinedRoomsWithPart(roomId, this.rankableRoomMap)) {
      if (this.isPlayedRoom(category, combined.docId) || this.isAffiliatedRoom(combined.docId)) {
        continue;
      }
      // The ranking may not reflect our own write yet.
      const prompt = combinedPrompt(combined, this.rankableRoomMap, id => id === roomId || this.isPlayedRoom(category, id));
      if (prompt && await this.askPlayedCombined(combined, prompt, room)) {
        await this.markPlayed(category, combined.docId);
      }
    }
  }

  // Returns false if they chose to keep the room marked.
  private async markUnplayedWithCombined(category: TerpecaCategory, roomId: string): Promise<boolean> {
    const room = this.rankableRoomMap.get(roomId);
    const playedParts = room ? listedParts(room, this.rankableRoomMap).filter(part => this.isPlayedRoom(category, part.docId)) : [];
    if (playedParts.length) {
      const parts = this.roomList(playedParts);
      const unmarkParts = await this.askDialog({
        message: `${this.roomName(room)} includes ${parts}. Did you also play ` +
          `${playedParts.length === 1 ? 'it' : 'them'} on ${playedParts.length === 1 ? 'its' : 'their'} own?`,
        okLabel: `No, unmark ${playedParts.length === 1 ? 'it' : 'them'} too`,
        cancelLabel: `Yes, keep ${playedParts.length === 1 ? 'it' : 'them'} marked`,
      });
      await this.markUnplayed(category, roomId, ...(unmarkParts ? playedParts.map(part => part.docId) : []));
      return true;
    }
    const playedCombined = combinedRoomsWithPart(roomId, this.rankableRoomMap)
      .filter(combined => this.isPlayedRoom(category, combined.docId));
    if (playedCombined.length) {
      const titles = this.roomList(playedCombined);
      const confirmed = await this.askDialog({
        message: `You've marked ${titles} as played, which includes ${this.roomName(room)}. ` +
          `If you didn't play ${this.roomName(room)}, you didn't play ${titles} either. Unmark both?`,
        okLabel: 'Unmark both',
        cancelLabel: 'Keep both marked',
      });
      if (!confirmed) {
        return false;
      }
    }
    await this.markUnplayed(category, roomId, ...playedCombined.map(combined => combined.docId));
    return true;
  }

  // Rankings seeded from earlier years or nominations can have a combined game marked without all of its parts.
  private async fixUpCombinedRooms(category: TerpecaCategory) {
    const fixes = (this.getRanking(category)?.unsortedIds || [])
      .map(id => this.rankableRoomMap.get(id))
      .filter(room => room?.partIds)
      .map(room => ({ room, parts: this.unplayedParts(category, room) }))
      .filter(fix => fix.parts.length);
    if (!fixes.length) {
      return;
    }
    const addedIds = new Set(fixes.flatMap(fix => fix.parts.map(part => part.docId)));
    await this.markPlayed(category, ...addedIds);
    await this.showCombinedNotice('Some of the rooms you had marked as played are combined games, which include other ' +
      'rooms we hadn\'t marked for you yet:<ul>' +
      fixes.map(fix => `<li>${this.roomName(fix.room)} includes ${this.roomList(fix.parts)}</li>`).join('') +
      '</ul>We\'ve marked those as played too. If you didn\'t actually play the combined version as a single ' +
      'playthrough, unmark it.');
  }

  private unplayedParts(category: TerpecaCategory, combined: TerpecaRoom): TerpecaRoom[] {
    return listedParts(combined, this.rankableRoomMap)
      .filter(part => !this.isPlayedRoom(category, part.docId) && !this.isAffiliatedRoom(part.docId));
  }

  private async askPlayedCombined(combined: TerpecaRoom, prompt: CombinedPrompt, lastPart: TerpecaRoom): Promise<boolean> {
    const name = roomDisplayName(combined);
    if (prompt === CombinedPrompt.ONE_PART) {
      return this.askDialog({
        message: `${this.roomName(lastPart)} is also part of a longer combined game, ${this.roomName(combined)}. ` +
          'Is there any chance you played it that way, booked and played as a single playthrough?',
        okLabel: `Yes, I played ${name}`,
        cancelLabel: `No, just ${roomDisplayName(lastPart)}`,
      });
    }
    const parts = listedParts(combined, this.rankableRoomMap);
    return this.askDialog({
      message: `You've marked ${this.roomList(parts)}, which can also be played together as a single experience called ` +
        `${this.roomName(combined)}. Did you play them together as ${this.roomName(combined)}? Only answer yes if you ` +
        'booked and played the combined version as a single playthrough; playing them separately doesn\'t count.',
      okLabel: 'Yes, I played the combined version',
      cancelLabel: 'No, I only played them separately',
    });
  }

  // Closing the dialog without choosing counts as the cautious answer.
  private async askDialog(data: { message: string, okLabel: string, cancelLabel: string }): Promise<boolean> {
    const result = await firstValueFrom(this.dialog.open(SimpleDialogComponent, { data: { title: 'Input needed', ...data } }).afterClosed());
    return result === true;
  }

  private async showCombinedNotice(message: string) {
    await firstValueFrom(
      this.dialog.open(SimpleDialogComponent, { data: { title: 'Rooms added', message, hideCancel: true } }).afterClosed());
  }

  private roomName(room: TerpecaRoom) {
    return `<b><i>${roomDisplayName(room)}</i></b>`;
  }

  private roomList(rooms: TerpecaRoom[]) {
    const names = rooms.map(room => this.roomName(room));
    return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }

  // The partner import (partnerimportdialog): which tracking sites to offer, and adding the rooms the voter picks.
  private async loadImportOptions() {
    try {
      this.importOptions = await this.partnerImport.loadOptions();
    } catch (error: unknown) {
      console.error('could not load the tracking sites for importing', error);
      this.importOptions = [];
    }
    this.refreshImportLines();
  }

  async openImportDialog(partnerId?: string) {
    await firstValueFrom(this.dialog.open(PartnerImportDialogComponent, {
      data: { target: this, options: this.importOptions, partnerId }, maxWidth: '800px', width: '95vw'
    }).afterClosed());
  }

  // The one import line on the select steps, worded for what the voter has set up: the catch-all list of sites when
  // they haven't linked any, that site when they've linked one, or all of them (starting with the first) when they've
  // linked several. Kept in fields rather than computed in the template, so the button isn't re-rendered (and clicks
  // lost) on every change detection.
  private refreshImportLines() {
    const sorted = sortImportOptions(this.importOptions);
    this.linkedImportSites = sorted.flatMap(option => {
      const partner = option.status === 'live' && !option.mock ? getTrackerPartner(option.partnerId) : undefined;
      const id = partner ? String(this.user?.[partner.field] || '') : '';
      return id ? [{ option, id }] : [];
    });
    this.allImportSiteNames = this.joinNames(sorted.map(option => option.label), 'or');
    this.linkedImportSiteNames = this.joinNames(this.linkedImportSites.map(site => site.option.label), 'and');
  }

  private joinNames(names: string[], conjunction: string): string {
    return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} ${conjunction} ${names[names.length - 1]}`;
  }

  isRoomPlayed(roomId: string): boolean {
    const room = this.rankableRoomMap.get(roomId);
    return Boolean(room && this.isPlayedRoom(room.category, roomId));
  }

  async addImportedRooms(roomIds: string[]) {
    for (const category of [TerpecaCategory.TOP_ROOM, TerpecaCategory.TOP_ONLINE_ROOM]) {
      const ids = roomIds.filter(id => this.rankableRoomMap.get(id)?.category === category);
      if (ids.length) {
        await this.markPlayed(category, ...ids);
        (category === TerpecaCategory.TOP_ROOM ? this.thirdFormGroup : this.fifthFormGroup)?.markAsDirty();
      }
    }
  }

  async confirmPlayedRooms(category: TerpecaCategory) {
    if (!this.hasPlayedRooms(category)) {
      await this.getFirestoreDoc(category).update({
        unsortedIds: []
      });
    }
    const onlinePrefix = category === TerpecaCategory.TOP_ONLINE_ROOM ? 'online ' : '';
    const dialogRef = this.dialog.open(ConfirmListDialogComponent, {
      data: {
        category: `${ category === TerpecaCategory.TOP_ONLINE_ROOM ? 'Online ' : ''}Rooms Played`,
        confirmNoneInstructions: `Please confirm that you have not played any ${ onlinePrefix }rooms that were listed as finalists.`,
        confirmInstructions: `Please confirm that the following is the complete list of finalist ${ onlinePrefix }rooms that you have played.`,
        roomList: this.playedRooms(category),
        numbered: false
      }
    });
    const subscription = dialogRef.afterClosed().subscribe(result => {
      if (result) {
        switch (category) {
          case TerpecaCategory.TOP_ROOM:
            this.thirdFormGroup.markAsPristine();
            break;
          case TerpecaCategory.TOP_ONLINE_ROOM:
            this.fifthFormGroup.markAsPristine();
            break;
        }
        this.stepper.selected.completed = true;
        this.stepper.next();
      }
      subscription.unsubscribe();
    });
  }

  private refreshStillToRank() {
    this.stillToRankRoomIds = (this.selectedRoomIds || [])
      .filter(id => !this.rankedRoomIds?.includes(id) && !this.unrankedRoomIds?.includes(id))
      .sort(compareUsingMap(this.rankableRoomMap, compareEntitiesByLocation));
  }

  // Called when a room is dropped into the ranked list, either from elsewhere in that list or from the still-to-rank list.
  async dropOnRanked(event: CdkDragDrop<string[], string[], string>) {
    if (event.previousContainer === event.container) {
      console.log(`Drag ix=${event.previousIndex} to ix=${event.currentIndex}`);
      moveItemInArray(this.rankedRoomIds, event.previousIndex, event.currentIndex);
    } else {
      console.log(`Place ${event.item.data} at ix=${event.currentIndex}`);
      this.rankedRoomIds.splice(event.currentIndex, 0, event.item.data);
      this.refreshStillToRank();
    }
    await this.saveRankingData();
  }

  // Called when a ranked room is dragged back to the still-to-rank list.
  async dropOnStillToRank(event: CdkDragDrop<string[], string[], string>) {
    if (event.previousContainer === event.container) {
      return;  // That list is kept sorted by location.
    }
    this.rankedRoomIds = this.rankedRoomIds.filter(rid => rid !== event.item.data);
    this.refreshStillToRank();
    await this.saveRankingData();
  }

  // Called when a still-to-rank room is given a rank number, or sent to the top or bottom of the ranked list.
  async placeRoom(roomId: string, rank: number) {
    rank = Math.max(1, Math.min(rank, this.rankedRoomIds.length + 1));
    console.log(`Place ${roomId} at ${rank}`);
    this.rankedRoomIds.splice(rank - 1, 0, roomId);
    this.refreshStillToRank();
    await this.saveRankingData();
  }

  async placeRoomNumberChange(roomId: string, target: HTMLInputElement) {
    const parsedValue = parseInt(target.value, 10);
    target.value = '';
    if (isNaN(parsedValue)) {
      return;
    }
    await this.placeRoom(roomId, parsedValue);
  }

  // Called when the rank number for a room is edited directly by the user.
  async rankNumberChange(oldIndex: number, target: HTMLInputElement) {
    const parsedValue = parseInt(target.value, 10);
    if (isNaN(parsedValue)) {
      console.error(`Bad rank ix=${oldIndex} "${target.value}"`);
      target.value = `${oldIndex + 1}`;  // Reset to old value
      return;
    }

    const rank = Math.max(1, Math.min(parsedValue, this.rankedRoomIds.length));
    console.log(`Edit rank ix=${oldIndex} to ${rank}`);
    target.value = `${rank}`;  // Reset to canonical rank value
    moveItemInArray(this.rankedRoomIds, oldIndex, rank - 1);
    await this.saveRankingData();
  }

  toggleRoomRanked(roomId: string) {
    const room = this.rankableRoomMap.get(roomId);
    if (!this.unrankedRoomIds.includes(roomId)) {
      const data: any = {
        ranker: this,
        room: room
      };
      this.dialog.open(UnrankRoomDialogComponent, { data });
    } else {
      this.rerankRoom(room);
    }
  }

  private removeRoomId(roomId: string) {
    if (this.selectedRoomIds) {
      this.selectedRoomIds = this.selectedRoomIds.filter(rid => rid !== roomId);
    }
    if (this.rankedRoomIds) {
      this.rankedRoomIds = this.rankedRoomIds.filter(rid => rid !== roomId);
    }
    if (this.unrankedRoomIds) {
      this.unrankedRoomIds = this.unrankedRoomIds.filter(rid => rid !== roomId);
    }
    if (this.unrankedReasonMap && this.unrankedReasonMap[roomId]) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.unrankedReasonMap[roomId];
    }
    this.refreshStillToRank();
  }

  async unrankRoom(room: TerpecaRoom, reason: UnrankedReasonEntry) {
    if (!this.selectedRoomIds.includes(room.docId) || this.unrankedRoomIds.includes(room.docId)) {
      return;
    }
    if (reason.reason === UnrankedReason.DID_NOT_PLAY) {
      // In this case we just want to remove it entirely from all the lists.
      this.removeRoomId(room.docId);
    } else if (reason.reason === UnrankedReason.CONFLICT_OF_INTEREST) {
      // In this case, we mark the affiliation too, which also removes it from all the lists.
      await this.markAffiliated(room.docId);
    } else {
      // For all other reasons, we keep it in the list but move it to the unranked section.
      this.rankedRoomIds = this.rankedRoomIds.filter(rid => rid !== room.docId);
      if (!this.unrankedRoomIds.includes(room.docId)) {
        this.unrankedRoomIds.push(room.docId);
        this.unrankedRoomIds.sort(compareUsingMap(this.rankableRoomMap, compareEntitiesByLocation));
      }
      this.unrankedReasonMap[room.docId] = reason;
      this.refreshStillToRank();
    }
    await this.saveRankingData();
  }

  // Moves an unranked room back to the still-to-rank list.
  async rerankRoom(room: TerpecaRoom) {
    if (this.unrankedRoomIds.includes(room.docId)) {
      this.unrankedRoomIds = this.unrankedRoomIds.filter(rid => rid !== room.docId);
    }
    if (this.unrankedReasonMap[room.docId]) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.unrankedReasonMap[room.docId];
    }
    this.refreshStillToRank();
    await this.saveRankingData();
  }

  async saveRankingData() {
    switch (this.currentCategory) {
      case TerpecaCategory.TOP_ROOM:
        this.fourthFormGroup.markAsDirty();
        break;
      case TerpecaCategory.TOP_ONLINE_ROOM:
        this.sixthFormGroup.markAsDirty();
        break;
    }
    await this.getFirestoreDoc(this.currentCategory).update({
      horrorPreference: this.user.horrorPreference || HorrorPreference.UNKNOWN,
      unsortedIds: this.selectedRoomIds,
      rankedIds: this.rankedRoomIds,
      unrankedReasonMap: this.unrankedReasonMap
    });
    switch (this.currentCategory) {
      case TerpecaCategory.TOP_ROOM:
        this.fourthFormGroup.markAsPristine();
        break;
      case TerpecaCategory.TOP_ONLINE_ROOM:
        this.sixthFormGroup.markAsPristine();
        break;
    }
  }

  validateRankings(category: TerpecaCategory) {
    const ranking: TerpecaRanking = category === TerpecaCategory.TOP_ROOM ? this.regularRanking : this.onlineRanking;
    const nominationIds: string[] = category === TerpecaCategory.TOP_ROOM ? this.regularNominationIds : this.onlineNominationsIds;
    const numUnsorted = ranking.unsortedIds ? ranking.unsortedIds.length : 0;
    const numRanked = ranking.rankedIds ? ranking.rankedIds.length : 0;
    const numUnranked = getUnrankedIds(ranking).length || 0;
    const categoryDescriptor = category === TerpecaCategory.TOP_ROOM ? 'in-person' : 'online';
    const categoryRankStep = category === TerpecaCategory.TOP_ROOM ? 4 : 6;
    if (numUnsorted > numRanked + numUnranked) {
      const numStillToRank = numUnsorted - numRanked - numUnranked;
      this.rankingErrors.push(
        `${numStillToRank} selected ${categoryDescriptor} room${numStillToRank === 1 ? ' is' : 's are'} still to rank, ` +
        `please revisit Step ${categoryRankStep}.`);
    }
    if (numUnsorted < numRanked + numUnranked) {
      this.rankingErrors.push(`More ${categoryDescriptor} rooms were ranked than you played, please revisit Step ${categoryRankStep}.`);
    }
    if (ranking.unsortedIds) {
      for (const roomId of ranking.unsortedIds) {
        const room = this.rankableRoomMap.get(roomId);
        if (!room) {
          this.rankingErrors.push(`One of or more of the ${categoryDescriptor} rooms you selected is no longer rankable, please revisit Step ${categoryRankStep}.`);
          break;
        }
      }
    }
    if (ranking.rankedIds) {
      for (const roomId of ranking.rankedIds) {
        const room = this.rankableRoomMap.get(roomId);
        if (!room) {
          this.rankingErrors.push(`One of or more of the ${categoryDescriptor} rooms you ranked is no longer rankable, please revisit Step ${categoryRankStep}.`);
          break;
        }
        if (!ranking.unsortedIds || !ranking.unsortedIds.includes(roomId)) {
          this.rankingErrors.push(`One of or more of the ${categoryDescriptor} rooms you selected has not been ranked, please revisit Step ${categoryRankStep}.`);
          break;
        }
      }
    }
    for (const roomId of nominationIds) {
      if (this.rankableRoomMap.get(roomId) && (!ranking.unsortedIds || !ranking.unsortedIds.includes(roomId))) {
        this.rankingErrors.push(`One of or more of the ${categoryDescriptor} rooms you nominated has not been ranked, please revisit Step ${categoryRankStep - 1}.`);
        break;
      }
    }
  }

  async confirmSubmitRankings() {
    this.dialog.open(SimpleDialogComponent, {
      data: {
        title: 'Confirm Your Rankings',
        message: 'IMPORTANT: By submitting these rankings, you are publicly acknowledging that:' +
        '<ul><li>you have personally played all the rooms you have ranked</li>' +
        '<li>you do not have any conflict of interest related to the games you have ranked</li></ul>' +
        'Note that if it is found later that you are not acting in good faith and have chosen to rank games for any other reason than ' +
        'helping the project, you will be disqualified from further participation in the project and your actions may damage the reputation ' +
        'of yourself and any company you may be associated with.',
        okCallback: () => { this.submitRankings(); },
        okLabel: 'Confirm and Submit',
      }
    });
  }

  async submitRankings() {
    await this.auth.submitRankings();
    window.scrollTo(0, 0);
  }

  async reopenRankings() {
    await this.auth.reopenRankings();
    this.furthestStepReached = this.reviewAndSubmitStepIndex;
    window.scrollTo(0, 0);
  }
}
